'use client';

import { useEffect, useRef } from 'react';
import { useToastStore } from '../model/toast-store';
import { useRealtimeAgentStore } from '@/shared/store/realtime-agent';

/**
 * 연결 상태 토스트 훅
 * WebSocket 연결/해제 및 오류 발생 시 토스트 표시
 */
export const useConnectionToast = () => {
  const showToast = useToastStore((s) => s.showToast);
  const wasConnectedRef = useRef(false);

  // 연결 상태 변화 감지
  useEffect(() => {
    const unsubscribe = useRealtimeAgentStore.subscribe(
      (state) => state.isConnected,
      (isConnected) => {
        // 연결됐다가 해제된 경우에만 토스트 표시
        if (wasConnectedRef.current && !isConnected) {
          // 동의 전(CoverModal/ConsentModal)에는 토스트 표시 안 함 - 자동 재연결만 처리
          const hasConsented = useRealtimeAgentStore.getState().hasConsented;
          if (hasConsented) {
            showToast({
              type: 'warning',
              message: '서비스 연결이 해제되었습니다',
              position: 'top',
              duration: 5000,
            });
          }
        }
        wasConnectedRef.current = isConnected;
      }
    );

    return () => unsubscribe();
  }, [showToast]);

  // 에러 발생 감지
  useEffect(() => {
    const unsubscribe = useRealtimeAgentStore.subscribe(
      (state) => state.lastError,
      (error) => {
        if (error) {
          showToast({
            type: 'error',
            message: error.message || '오류가 발생했습니다',
            position: 'top',
            duration: 5000,
          });
        }
      }
    );

    return () => unsubscribe();
  }, [showToast]);

  // 재연결 상태 감지
  useEffect(() => {
    const unsubscribe = useRealtimeAgentStore.subscribe(
      (state) => state.isReconnecting,
      (isReconnecting) => {
        if (isReconnecting) {
          // 동의 전(CoverModal/ConsentModal)에는 토스트 표시 안 함 - 자동 재연결만 처리
          const hasConsented = useRealtimeAgentStore.getState().hasConsented;
          if (hasConsented) {
            showToast({
              type: 'info',
              message: '재연결 시도 중...',
              position: 'top',
              duration: 3000,
            });
          }
        }
      }
    );

    return () => unsubscribe();
  }, [showToast]);

  // 영구 연결 실패 감지
  useEffect(() => {
    const unsubscribe = useRealtimeAgentStore.subscribe(
      (state) => state.hasFailedPermanently,
      (hasFailedPermanently) => {
        if (hasFailedPermanently) {
          showToast({
            type: 'error',
            message: '서비스에 연결할 수 없습니다. 잠시 후 다시 시도해주세요.',
            position: 'top',
            duration: 0, // 수동 해제
          });
        }
      }
    );

    return () => unsubscribe();
  }, [showToast]);
};
